import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb, formatDateHourDb } from "../utils/tools";
import { customDateFormatType, formatDateForInput } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.attendanceSummaryList;
const queryKey = "attendanceSummaryList";

async function fetchAttendanceSummaryList(currentPage, param) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (param?.employeeId) options.employeeId = param.employeeId;
    if (param?.type) options.type = param.type;

    if (param?.saleBlockId) options.saleBlockId = param.saleBlockId;
    if (param?.saleAreaId) options.saleAreaId = param.saleAreaId;
    if (param?.supportDepartmentId)
      options.supportDepartmentId = param.supportDepartmentId;
    if (param?.branchId) options.branchId = param.branchId;
    if (param?.department !== undefined && param?.department !== null)
      options.department = param.department; // 0|1|2

    // time filters
    if (param?.day) options.day = customDateFormatType(param.day);
    if (param?.month) options.month = param.month; // "MM-YYYY"
    if (param?.year) options.year = param.year;

    if (param?.startTime)
      options.startTime = customDateFormatType(param.startTime);
    if (param?.endTime) options.endTime = customDateFormatType(param.endTime);

    // statuses (optional)
    if (param?.registerStatus) options.registerStatus = param.registerStatus;
    if (param?.leaveStatus !== undefined && param?.leaveStatus !== null)
      options.leaveStatus = param.leaveStatus;

    if (param?.employeeStatus !== undefined && param?.employeeStatus !== null)
      options.employeeStatus = param.employeeStatus; // 0|1

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI(
      "get",
      `/checkin/attendance-summary/list?${queryParams}`
    );
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?.employeeId || "",
        employeeCode: item?.employeeCode || "-",
        employeeFullName: item?.employeeFullName || "-",
        saleArea: item?.saleArea?.name || "-",
        saleBlock: item?.saleBlock?.name || "-",
        branch: item?.branch?.name || "-",
        supportDepartment: item?.supportDepartment?.name || "-",
        date: item?.dateLabel || "",
        categoryLabel: item?.categoryLabel || "-",
        edit: {
          id: item?.employeeId || "",
          type: "ALL",
        },
        detail: {
          id: item?.employeeId || "",
          type: "ALL",
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useAttendanceSummaryList(param) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, currentPage, param],
    queryFn: () => fetchAttendanceSummaryList(currentPage, param),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    attendanceSummaryListData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
